import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Fullscreen horizontal lightbox gallery with Fancybox-style zoom.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media slide elements.
 * @property {HTMLElement} thumbnails - The thumbnails container.
 * @property {HTMLElement} [gallery] - The horizontal scroll gallery.
 * @property {HTMLButtonElement} [previous] - Previous slide button.
 * @property {HTMLButtonElement} [next] - Next slide button.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  /** @type {number} */
  #activeIndex = 0;
  /** @type {AbortController | null} */
  #scrollController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindGalleryScroll();
    this.addEventListener('drag-zoom:change', this.#handleZoomChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollController?.abort();
    this.removeEventListener('drag-zoom:change', this.#handleZoomChange);
  }

  /**
   * Called after the component DOM is morph-updated (variant changes).
   */
  updatedCallback() {
    this.#bindGalleryScroll();
  }

  #bindGalleryScroll() {
    this.#scrollController?.abort();
    this.#scrollController = new AbortController();

    const gallery = this.#getGallery();
    if (!gallery) return;

    gallery.addEventListener('scroll', this.handleScroll, {
      passive: true,
      signal: this.#scrollController.signal,
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getGallery() {
    return this.refs.gallery ?? this.querySelector('.dialog-zoomed-gallery');
  }

  /**
   * Opens the zoom dialog on a specific media index.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails?.children?.[index];

    this.#activeIndex = index;
    this.#resetAllZoom();

    const open = () => {
      dialog.showModal();
      this.#updateNavButtons();

      for (const target of [targetThumbnail, targetImage]) {
        if (target instanceof HTMLElement) {
          target.scrollIntoView({ behavior: 'instant', inline: 'center', block: 'nearest' });
        }
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Syncs active thumbnail while the user swipes the horizontal gallery.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;
    if (!media?.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);

    if (activeIndex < 0 || activeIndex === this.#activeIndex) {
      this.#loadHighResolutionImage(mostVisibleElement);
      return;
    }

    // Reset zoom when the visible slide changes via swipe
    this.#resetAllZoom();
    this.#activeIndex = activeIndex;
    this.#updateNavButtons();

    if (thumbnails) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      });

      const targetThumbnail = thumbnails.children[activeIndex];
      if (targetThumbnail instanceof HTMLElement) {
        scrollIntoView(targetThumbnail, {
          ancestor: thumbnails,
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    this.#resetAllZoom();

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Keyboard: Escape closes; arrows navigate when not typing.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  }

  /**
   * Navigate to the previous slide.
   */
  previous() {
    if (this.#activeIndex <= 0) return;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(this.#activeIndex - 1, { behavior });
  }

  /**
   * Navigate to the next slide.
   */
  next() {
    const { media } = this.refs;
    if (!media?.length || this.#activeIndex >= media.length - 1) return;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(this.#activeIndex + 1, { behavior });
  }

  /**
   * Toolbar zoom in on the active image.
   */
  zoomIn() {
    this.#getActiveZoomWrapper()?.zoomIn();
  }

  /**
   * Toolbar zoom out on the active image.
   */
  zoomOut() {
    this.#getActiveZoomWrapper()?.zoomOut();
  }

  /**
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * @param {number} index
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Selects a slide by index (thumbnails, arrows, or keyboard).
   * @param {number} index
   * @param {{ behavior?: ScrollBehavior }} [options]
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;
    if (!media?.length) return;
    if (isNaN(index) || index < 0 || index >= media.length) return;

    if (index !== this.#activeIndex) {
      this.#resetAllZoom();
    }

    this.#activeIndex = index;
    this.#updateNavButtons();

    if (thumbnails?.children?.length) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });

      const targetThumbnail = thumbnails.children[index];
      if (targetThumbnail instanceof HTMLElement) {
        scrollIntoView(targetThumbnail, {
          ancestor: thumbnails,
          behavior: options.behavior,
          block: 'center',
          inline: 'center',
        });
      }
    }

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
        inline: 'center',
        block: 'nearest',
      });

      this.#loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #updateNavButtons() {
    const { media, previous, next } = this.refs;
    const count = media?.length ?? 0;

    if (previous) {
      previous.disabled = this.#activeIndex <= 0 || count <= 1;
      previous.hidden = count <= 1;
    }

    if (next) {
      next.disabled = this.#activeIndex >= count - 1 || count <= 1;
      next.hidden = count <= 1;
    }
  }

  #resetAllZoom() {
    const wrappers = this.querySelectorAll('drag-zoom-wrapper');
    for (const wrapper of wrappers) {
      if (typeof /** @type {import('./drag-zoom-wrapper').DragZoomWrapper} */ (wrapper).resetZoom === 'function') {
        /** @type {import('./drag-zoom-wrapper').DragZoomWrapper} */ (wrapper).resetZoom();
      }
    }

    this.#getGallery()?.classList.remove('dialog-zoomed-gallery--zoomed');
  }

  /**
   * @returns {import('./drag-zoom-wrapper').DragZoomWrapper | null}
   */
  #getActiveZoomWrapper() {
    const slide = this.refs.media?.[this.#activeIndex];
    if (!slide) return null;
    return /** @type {import('./drag-zoom-wrapper').DragZoomWrapper | null} */ (
      slide.querySelector('drag-zoom-wrapper')
    );
  }

  /**
   * Locks horizontal slide scrolling while an image is zoomed (Fancybox behavior).
   * @param {Event} event
   */
  #handleZoomChange = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const gallery = this.#getGallery();
    if (!gallery) return;

    const zoomed = Boolean(event.detail?.zoomed);
    gallery.classList.toggle('dialog-zoomed-gallery--zoomed', zoomed);
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * @param {HTMLElement[]} elements
 * @returns {Promise<HTMLElement>}
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
